import { rs, fs, vs } from './responsive';

/** lightColors - light theme color palette with WCAG AA verified contrast ratios. */
export const lightColors = {
  // Primary — deep teal for buttons, links, interactive elements
  primary: '#245f73',
  primaryDark: '#1b4a5a',
  primaryLight: '#347d96',
  primaryBg: '#dceef4',

  // Accent — warm brown for highlights, badges, emphasis
  secondary: '#733e24',
  secondaryLight: '#8b5236',
  secondaryBg: '#f0e4db',

  // Danger — warm red for stop, sign out, delete
  danger: '#c0392b',
  dangerDark: '#962d22',
  dangerLight: '#e74c3c',

  success: '#245f73',
  warning: '#d4930d',

  // Backgrounds
  background: '#bbbdbc',
  surface: '#f2f0ef',
  surfaceSecondary: '#e8e6e4',

  // Text — WCAG AA contrast ratios verified
  text: '#1a1a1a',                // 15.3:1 on surface
  textSecondary: '#4a4a4a',       // 7.5:1 on surface (upgraded from #555)
  textLight: '#666666',           // 5.7:1 on surface (upgraded from #888)

  // Borders
  border: '#c8c8c8',
  borderLight: '#dddcda',

  // Utility
  accent: '#733e24',
  accentSecondary: '#245f73',
  white: '#ffffff',
  pureWhite: '#ffffff',
  black: '#000000',

  // Focus indicator (Guideline 5: Accessibility)
  focusRing: '#245f73',
};

/** darkColors - dark theme color palette for OLED/dark-mode displays. */
export const darkColors: typeof lightColors = {
  primary: '#4fa8c4',
  primaryDark: '#347d96',
  primaryLight: '#6fc4de',
  primaryBg: '#1a3a45',

  secondary: '#c4845c',
  secondaryLight: '#d49a76',
  secondaryBg: '#3a2a1e',

  danger: '#e74c3c',
  dangerDark: '#c0392b',
  dangerLight: '#ff6b5a',

  success: '#4fa8c4',
  warning: '#f0b429',

  background: '#121212',
  surface: '#1e1e1e',
  surfaceSecondary: '#2a2a2a',

  text: '#e8e8e8',
  textSecondary: '#b0b0b0',
  textLight: '#888888',

  border: '#3a3a3a',
  borderLight: '#2e2e2e',

  accent: '#c4845c',
  accentSecondary: '#4fa8c4',
  white: '#ffffff',
  pureWhite: '#ffffff',
  black: '#000000',

  focusRing: '#4fa8c4',
};

export type ThemeMode = 'light' | 'dark' | 'system';
export type ColorTheme = typeof lightColors;

/** getColorsForMode - returns light or dark color palette based on mode flag. */
export function getColorsForMode(isDark: boolean): ColorTheme {
  return isDark ? darkColors : lightColors;
}

export const spacing = {
  xs: rs(4),
  sm: rs(8),
  md: rs(16),
  lg: rs(24),
  xl: rs(32),
  xxl: rs(48),
};

export const borderRadius = {
  sm: rs(8),
  md: rs(12),
  lg: rs(16),
  xl: rs(24),
  full: 9999,
};

export const fontSize = {
  xs: fs(12),
  sm: fs(14),
  md: fs(16),
  lg: fs(19),
  xl: fs(24),
  xxl: fs(32),
  hero: fs(40),
};

export const fontWeight = {
  normal: '400' as const,
  medium: '500' as const,
  semibold: '600' as const,
  bold: '700' as const,
};

// Guideline 5: Accessibility — minimum touch target size (48x48 dp)
export const accessibility = {
  minTouchTarget: 48,
  focusRingWidth: 2,
};
